import React, { useEffect, useState } from "react";
import { Star, BadgeCheck, ShieldCheck, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useCountry } from "../lib/country";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Public site reviews — reviews about the WHOLE site (not products).
 * Country-aware: shows reviews for the active country (SV or US).
 */
export default function SiteReviews() {
    const { country } = useCountry();
    const { user } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form state
    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [mySubmitted, setMySubmitted] = useState(false);

    const isSV = country === "SV";
    const t = (sv, us) => isSV ? sv : us;

    const load = async () => {
        try {
            const { data } = await api.get(`/site-reviews?country=${country}`);
            setReviews(data || []);
            if (user) {
                const { data: mine } = await api.get("/site-reviews/mine");
                setMySubmitted((mine || []).some((r) => r.country === country));
            }
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [country, user]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (rating < 1) { toast.error(t("Elige una calificación", "Pick a rating")); return; }
        if (content.trim().length < 10) { toast.error(t("Mínimo 10 caracteres", "Min 10 characters")); return; }
        setSubmitting(true);
        try {
            await api.post("/site-reviews", { rating, title: title.trim(), content: content.trim(), country });
            toast.success(t("¡Gracias! Tu reseña será revisada antes de publicarse.", "Thanks! Your review will be reviewed before publishing."));
            setRating(0); setTitle(""); setContent("");
            setMySubmitted(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
        } finally { setSubmitting(false); }
    };

    const avg = reviews.length > 0
        ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
        : null;

    return (
        <div className="max-w-4xl mx-auto px-6 py-12">
            <div className="text-center mb-10">
                <div className="text-5xl mb-2">{isSV ? "🇸🇻" : "🇺🇸"}</div>
                <h1 className="font-display text-4xl mb-2">
                    {t("Reseñas del sitio", "Site reviews")}
                </h1>
                <p className="text-sm text-[var(--tb-text-2)]">
                    {t(
                        "Experiencias reales de clientes de Terra Boutique en El Salvador.",
                        "Real experiences from Terra Boutique customers in the United States.",
                    )}
                </p>
                {avg && (
                    <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--tb-border-gold)]/15 border border-[var(--tb-gold)]/30">
                        <Star className="w-4 h-4 text-[var(--tb-gold)] fill-[var(--tb-gold)]" />
                        <span className="text-lg font-display text-[var(--tb-gold)]">{avg}</span>
                        <span className="text-xs text-[var(--tb-text-2)]">/ 5 · {reviews.length} {t("reseñas", "reviews")}</span>
                    </div>
                )}
            </div>

            {/* Submission form */}
            {!user ? (
                <div className="tb-card p-6 mb-10 text-center">
                    <p className="text-sm text-[var(--tb-text-2)]">
                        {t(
                            "Inicia sesión para compartir tu experiencia.",
                            "Sign in to share your experience.",
                        )}
                    </p>
                    <Link to="/login" className="tb-btn-primary mt-3 inline-block">{t("Acceder", "Sign in")}</Link>
                </div>
            ) : mySubmitted ? (
                <div className="tb-card p-6 mb-10 flex items-center gap-3 border-emerald-500/30">
                    <ShieldCheck className="w-6 h-6 text-emerald-400" />
                    <p className="text-sm text-[var(--tb-text-2)]">
                        {t(
                            "Ya enviaste tu reseña para este país. ¡Gracias por contribuir!",
                            "You already submitted your review for this country. Thanks for contributing!",
                        )}
                    </p>
                </div>
            ) : (
                <form onSubmit={submit} className="tb-card p-6 mb-10 space-y-4" data-testid="site-review-form">
                    <h2 className="font-display text-xl">
                        {t("Comparte tu experiencia", "Share your experience")}
                    </h2>
                    {/* Stars */}
                    <div>
                        <div className="tb-overline mb-1">{t("Calificación", "Rating")}</div>
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button key={n} type="button" onClick={() => setRating(rating === n ? 0 : n)}
                                    className="transition hover:scale-110" data-testid={`review-star-${n}`}>
                                    <Star className={`w-8 h-8 ${n <= rating ? "text-[var(--tb-gold)] fill-[var(--tb-gold)]" : "text-[var(--tb-muted)]"}`} />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="tb-overline">{t("Título (opcional)", "Title (optional)")}</label>
                        <input className="tb-input mt-1" placeholder={t("Ej. Atención impecable", "e.g. Outstanding service")}
                            value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                            data-testid="review-title" />
                    </div>
                    <div>
                        <label className="tb-overline">{t("Tu reseña *", "Your review *")}</label>
                        <textarea className="tb-input mt-1 min-h-[120px]"
                            placeholder={t(
                                "Cuéntanos sobre tu experiencia comprando en Terra Boutique...",
                                "Tell us about your shopping experience at Terra Boutique...",
                            )}
                            value={content} onChange={(e) => setContent(e.target.value)} maxLength={2000}
                            data-testid="review-content" />
                        <div className="text-[10px] text-right text-[var(--tb-muted)] mt-0.5">{content.length}/2000</div>
                    </div>
                    <p className="text-[10px] text-[var(--tb-muted)]">
                        {t("Las reseñas son moderadas antes de publicarse.", "Reviews are moderated before publishing.")}
                    </p>
                    <button type="submit" disabled={submitting || rating < 1 || content.trim().length < 10}
                        className="tb-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="review-submit">
                        <Send className="w-4 h-4" /> {submitting ? t("Enviando...", "Sending...") : t("Publicar reseña", "Submit review")}
                    </button>
                </form>
            )}

            {/* Reviews list */}
            <div>
                <h2 className="font-display text-2xl mb-4">
                    {t("Lo que dicen nuestros clientes", "What our customers say")}
                </h2>
                {loading ? (
                    <div className="tb-card p-8 text-center text-sm text-[var(--tb-text-2)]">{t("Cargando...", "Loading...")}</div>
                ) : reviews.length === 0 ? (
                    <div className="tb-card p-10 text-center" data-testid="site-reviews-empty">
                        <Star className="w-10 h-10 mx-auto text-[var(--tb-muted)] mb-2" />
                        <p className="text-sm text-[var(--tb-text-2)]">
                            {t("Aún no hay reseñas publicadas. ¡Sé el primero!", "No published reviews yet. Be the first!")}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="site-reviews-list">
                        {reviews.map((r) => (
                            <article key={r._id} className="tb-card p-5" data-testid={`review-${r._id}`}>
                                <header className="flex items-center gap-3 mb-2">
                                    {r.user_photo ? (
                                        <img src={r.user_photo} alt="" className="w-10 h-10 rounded-full object-cover border border-[var(--tb-border)]" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-[var(--tb-bg-2)] border border-[var(--tb-border)] flex items-center justify-center font-display">
                                            {(r.user_name || "?").charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="font-medium text-sm truncate">{r.user_name}</span>
                                            {r.user_verified && <BadgeCheck className="w-4 h-4 text-[#2196F3] fill-[#2196F3]/20" />}
                                            <span className="text-[10px] text-[var(--tb-muted)]">· {r.country === "US" ? "🇺🇸" : "🇸🇻"}</span>
                                        </div>
                                        <div className="flex items-center gap-1 mt-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "text-[var(--tb-gold)] fill-[var(--tb-gold)]" : "text-[var(--tb-muted)]"}`} />
                                            ))}
                                            <span className="text-[10px] text-[var(--tb-muted)] ml-2">
                                                {new Date(r.created_at).toLocaleDateString(isSV ? "es-SV" : "en-US")}
                                            </span>
                                        </div>
                                    </div>
                                </header>
                                {r.title && <h3 className="font-medium text-sm mb-1">{r.title}</h3>}
                                <p className="text-sm text-[var(--tb-text-2)] leading-relaxed whitespace-pre-line">{r.content}</p>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
